import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert, Spinner, Pagination } from 'react-bootstrap';
import { apiService } from '../../services/api';

interface ParkingRecord {
  id: string;
  plateNumber: string;
  spaceNumber: string;
  entryTime: string;
  exitTime?: string;
  duration?: string;
  fee?: number;
  status: 'active' | 'completed';
  paymentStatus: 'pending' | 'paid' | 'overdue';
}

const ParkingRecords: React.FC = () => {
  const [records, setRecords] = useState<ParkingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchPlate, setSearchPlate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const recordsPerPage = 10;

  // 載入停車記錄數據
  const loadParkingRecords = async () => {
    try {
      setLoading(true);
      setError('');
      
      // 從API獲取停車記錄數據
      const response = await apiService.getParkingRecords({
        page: currentPage,
        limit: recordsPerPage,
        search: searchPlate,
        status: statusFilter === 'all' ? undefined : statusFilter
      });
      
      if (response.success && response.data) {
        // 將API數據格式轉換為前端格式
        const formattedRecords: ParkingRecord[] = response.data.items.map((record: any) => ({
          id: record._id || record.id,
          plateNumber: record.vehicle?.licensePlate || record.plateNumber || 'N/A',
          spaceNumber: record.parkingSpot?.spotNumber || record.spaceNumber || 'N/A',
          entryTime: new Date(record.entryTime).toLocaleString('zh-TW'),
          exitTime: record.exitTime ? new Date(record.exitTime).toLocaleString('zh-TW') : undefined,
          duration: record.duration ? `${Math.floor(record.duration / 60)}小時${record.duration % 60}分鐘` : undefined,
          fee: record.totalFee || record.fee,
          status: record.status === 'active' ? 'active' : 'completed',
          paymentStatus: record.payment?.status || record.paymentStatus || 'pending'
        }));
        
        setRecords(formattedRecords);
      } else {
        throw new Error(response.message || '獲取停車記錄失敗');
      }
    } catch (error: any) {
      console.error('載入停車記錄失敗:', error);
      setError('載入停車記錄數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParkingRecords();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'success';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '進行中';
      case 'completed': return '已完成';
      default: return '未知';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'danger';
      default: return 'secondary';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'paid': return '已付款';
      case 'pending': return '待付款';
      case 'overdue': return '逾期';
      default: return '未知';
    }
  };

  // 過濾記錄
  const filteredRecords = records.filter(record => {
    const matchesPlate = record.plateNumber.toLowerCase().includes(searchPlate.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    return matchesPlate && matchesStatus;
  });

  // 分頁
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-history me-3"></i>
          停車記錄
        </h1>
        <Button variant="primary" onClick={loadParkingRecords}>
          <i className="fas fa-sync-alt me-2"></i>
          重新整理
        </Button>
      </div>

      {/* 搜尋和過濾 */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>搜尋車牌號碼</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="請輸入車牌號碼"
                  value={searchPlate}
                  onChange={(e) => setSearchPlate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>狀態篩選</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">全部狀態</option>
                  <option value="active">進行中</option>
                  <option value="completed">已完成</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 記錄表格 */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">停車記錄列表 ({filteredRecords.length} 筆記錄)</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {currentRecords.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">沒有找到符合條件的記錄</p>
            </div>
          ) : (
            <Table responsive striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th>車牌號碼</th>
                  <th>停車位</th>
                  <th>進入時間</th>
                  <th>離開時間</th>
                  <th>停車時長</th>
                  <th>費用</th>
                  <th>狀態</th>
                  <th>付款狀態</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="fw-bold">{record.plateNumber}</td>
                    <td>{record.spaceNumber}</td>
                    <td>{record.entryTime}</td>
                    <td>{record.exitTime || '-'}</td>
                    <td>{record.duration || '-'}</td>
                    <td>
                      {record.fee ? (
                        <span className="fw-bold text-success">NT$ {record.fee}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <Badge bg={getStatusColor(record.status)}>
                        {getStatusText(record.status)}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={getPaymentStatusColor(record.paymentStatus)}>
                        {getPaymentStatusText(record.paymentStatus)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (
                page === currentPage - 3 ||
                page === currentPage + 3
              ) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}
            
            <Pagination.Next 
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ParkingRecords;